// Draw a multi-round competition's groups so that everyone plays with as many
// different people as possible.
//
// "Everyone plays with everyone" is often not possible, and the screen has to
// say so rather than pretend: a player meets (groupSize - 1) others a round, so
// twelve players in fourballs over three rounds meet nine of their eleven
// playing partners at most. So this is an optimisation, not a construction —
// it MINIMISES REPEATS: the cost of a draw is the sum, over every pair of
// players, of pairCost(times they share a group). Two players who never meet
// cost STRANGER; a pair who meets m > 0 times costs (m - 1)², so meeting once
// is free and every repeat costs more than the one before. STRANGER outweighs
// a pair's second, third or fourth meeting, which makes the order of priorities the one asked for: get
// everyone round everyone first, then spread the repeats that pays for as
// thinly as they go. (Plain m² cannot tell those apart — for 8 players in
// fourballs over 3 rounds it scores "everyone meets everyone" and "everyone
// misses one" identically.) Where a perfect draw exists (16 in fourballs over
// 5 rounds) the search finds it; where it does not, it gets as close as it can.
//
// FAIR TO EACH PLAYER, NOT JUST IN TOTAL. Minimising strangers over the whole
// field let the search park them on a few people: 18 in fourballs over four
// rounds could give everyone 12 different partners, and the draw gave one
// player 8 while others had 12. So each player's own count of people they
// never meet also costs FAIR × its square — squared, so a stranger moved from
// someone missing five people to someone missing two makes the draw cheaper.
// Measured over the field sizes a society actually has, that took the gap
// between the best- and worst-off player from up to four partners to one.
//
// Rounds already played (or locked) are HISTORY, not free: who partnered whom
// there counts against pairing them again, so mixing after Round 1 still
// spreads Rounds 2 and 3 around what actually happened.

// Group sizes for n players in groups of at most `size`: as few groups as
// that allows, as level as they will go — 10 in fourballs is 4-3-3, not
// 4-4-2, and 13 is 4-3-3-3 rather than three fourballs and a single who
// would meet nobody that round.
export const groupSizes = (n, size) => {
    if (n <= 0 || size <= 0) return [];
    const count = Math.ceil(n / size);
    const small = Math.floor(n / count);
    const bigger = n - small * count;
    return Array.from({ length: count }, (_, i) => (i < bigger ? small + 1 : small));
};

const STRANGER = 10;
const FAIR = 8;
const pairCost = (m) => (m === 0 ? STRANGER : (m - 1) * (m - 1));
// Cost of one more / one fewer meeting for a pair currently on m.
const gain = (m) => (m === 0 ? -STRANGER : 2 * m - 1);
const loss = (m) => (m === 1 ? STRANGER : 3 - 2 * m);

const shuffle = (arr, random) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// playerIds: the roster. fixedRounds: [[id, ...], ...] per round — groups that
// may not move. freeRounds: how many rounds to draw. Returns one array of
// groups (arrays of ids) per free round, in order.
export const mixRounds = ({
    playerIds,
    groupSize,
    freeRounds,
    fixedRounds = [],
    random = Math.random,
    restarts = 12,
    iterations = 4000,
    fair = FAIR,
}) => {
    const n = playerIds.length;
    if (n === 0 || freeRounds === 0) return Array.from({ length: freeRounds }, () => []);
    const index = new Map(playerIds.map((id, i) => [id, i]));
    const sizes = groupSizes(n, groupSize);

    const base = new Int32Array(n * n);
    for (const round of fixedRounds) {
        for (const group of round) {
            const members = group.map(id => index.get(id)).filter(i => i !== undefined);
            for (let x = 0; x < members.length; x++) {
                for (let y = x + 1; y < members.length; y++) {
                    base[members[x] * n + members[y]]++;
                    base[members[y] * n + members[x]]++;
                }
            }
        }
    }

    // The full score of a finished draw — pair costs plus the fairness term —
    // which is what decides between restarts, however each was searched.
    const drawCost = (draw) => {
        const met = base.slice();
        for (const groups of draw) {
            for (const g of groups) {
                for (let x = 0; x < g.length; x++) {
                    for (let y = x + 1; y < g.length; y++) {
                        met[g[x] * n + g[y]]++;
                        met[g[y] * n + g[x]]++;
                    }
                }
            }
        }
        const strangers = new Int32Array(n);
        let total = 0;
        for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
                total += pairCost(met[i * n + j]);
                if (met[i * n + j] === 0) { strangers[i]++; strangers[j]++; }
            }
        }
        for (let i = 0; i < n; i++) total += fair * strangers[i] ** 3;
        return total;
    };

    let best = null;
    let bestCost = Infinity;
    const steps = iterations * freeRounds;

    for (let attempt = 0; attempt < restarts; attempt++) {
        // HALF THE RESTARTS SEARCH WITHOUT THE FAIRNESS TERM. Its steep cost
        // makes the landscape harder to cross, and on its own it stopped
        // finding the perfect draws (16 in fourballs over five rounds) that
        // plain search finds every time. A perfect draw has no strangers, so
        // it scores nothing for fairness and wins on the full score anyway.
        const w = attempt % 2 === 0 ? 0 : fair;
        const met = base.slice();
        const rounds = [];
        for (let r = 0; r < freeRounds; r++) {
            const order = shuffle([...Array(n).keys()], random);
            const groups = [];
            let at = 0;
            for (const s of sizes) { groups.push(order.slice(at, at + s)); at += s; }
            for (const g of groups) {
                for (let x = 0; x < g.length; x++) {
                    for (let y = x + 1; y < g.length; y++) {
                        met[g[x] * n + g[y]]++;
                        met[g[y] * n + g[x]]++;
                    }
                }
            }
            rounds.push(groups);
        }

        // strangers[p]: how many of the field p never shares a group with.
        const strangers = new Int32Array(n);
        let cost = 0;
        for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
                cost += pairCost(met[i * n + j]);
                if (met[i * n + j] === 0) { strangers[i]++; strangers[j]++; }
            }
        }
        for (let i = 0; i < n; i++) cost += w * strangers[i] ** 3;
        let attemptBest = rounds.map(gs => gs.map(g => [...g]));
        let attemptCost = cost;
        const shift = new Int32Array(n);   // per-swap change to strangers[]
        const marked = new Uint8Array(n);
        const touched = [];
        const note = (p, by) => {
            if (!marked[p]) { marked[p] = 1; touched.push(p); }
            shift[p] += by;
        };

        // One group a round means nothing can move.
        if (sizes.length > 1) {
            // Annealed swaps: a swap that adds repeats is taken now and then
            // while the temperature is high, which is what gets the search out
            // of draws where every single swap makes things worse.
            for (let step = 0; step < steps; step++) {
                const temperature = (4 + 2 * w) * (1 - step / steps) + 0.01;
                const groups = rounds[Math.floor(random() * freeRounds)];
                const ga = Math.floor(random() * groups.length);
                let gb = Math.floor(random() * (groups.length - 1));
                if (gb >= ga) gb++;
                const A = groups[ga];
                const B = groups[gb];
                const ia = Math.floor(random() * A.length);
                const ib = Math.floor(random() * B.length);
                const a = A[ia];
                const b = B[ib];

                // Each pair touched is distinct, so the deltas simply add. A
                // pair leaving 1 makes both players a stranger to each other;
                // a pair leaving 0 makes them partners.
                let delta = 0;
                for (const x of A) {
                    if (x === a) continue;
                    const lost = met[a * n + x];
                    const won = met[b * n + x];
                    delta += loss(lost) + gain(won);
                    if (lost === 1) { note(a, 1); note(x, 1); }
                    if (won === 0) { note(b, -1); note(x, -1); }
                }
                for (const y of B) {
                    if (y === b) continue;
                    const lost = met[b * n + y];
                    const won = met[a * n + y];
                    delta += loss(lost) + gain(won);
                    if (lost === 1) { note(b, 1); note(y, 1); }
                    if (won === 0) { note(a, -1); note(y, -1); }
                }
                for (const p of touched) {
                    const was = strangers[p];
                    const now = was + shift[p];
                    delta += w * (now ** 3 - was ** 3);
                }
                const accept = delta <= 0 || random() < Math.exp(-delta / temperature);
                for (const p of touched) {
                    if (accept) strangers[p] += shift[p];
                    shift[p] = 0;
                    marked[p] = 0;
                }
                touched.length = 0;
                if (!accept) continue;

                for (const x of A) {
                    if (x === a) continue;
                    met[a * n + x]--; met[x * n + a]--;
                    met[b * n + x]++; met[x * n + b]++;
                }
                for (const y of B) {
                    if (y === b) continue;
                    met[b * n + y]--; met[y * n + b]--;
                    met[a * n + y]++; met[y * n + a]++;
                }
                A[ia] = b;
                B[ib] = a;
                cost += delta;
                if (cost < attemptCost) {
                    attemptCost = cost;
                    attemptBest = rounds.map(gs => gs.map(g => [...g]));
                }
            }
        }

        const score = drawCost(attemptBest);
        if (score < bestCost) {
            bestCost = score;
            best = attemptBest;
        }
    }

    return best.map(gs => gs.map(g => g.map(i => playerIds[i])));
};

// How well a set of rounds mixes the field. rounds: [[ [id...], ...], ...].
// `enoughRounds` is false when everyone-meets-everyone is arithmetically out
// of reach — a player meets at most (groupSize - 1) others a round — so the
// screen can say the rounds are the limit rather than leave the organiser
// wondering whether to press the button again. True does NOT promise a
// perfect draw exists (12 in fourballs is stuck repeating from Round 2 on).
export const pairingCoverage = (playerIds, rounds, groupSize) => {
    const n = playerIds.length;
    const ids = new Set(playerIds);
    const partners = new Map(playerIds.map(id => [id, new Map()]));
    for (const round of rounds) {
        for (const group of round) {
            const members = group.filter(id => ids.has(id));
            for (const a of members) {
                for (const b of members) {
                    if (a === b) continue;
                    const seen = partners.get(a);
                    seen.set(b, (seen.get(b) || 0) + 1);
                }
            }
        }
    }
    const counts = playerIds.map(id => partners.get(id).size);
    const maxRepeat = Math.max(1, ...playerIds.flatMap(id => [...partners.get(id).values()]));
    const minMet = n > 0 ? Math.min(...counts) : 0;
    const maxMet = n > 0 ? Math.max(...counts) : 0;
    return {
        minMet,
        maxMet,
        others: Math.max(0, n - 1),
        rounds: rounds.length,
        enoughRounds: rounds.length * Math.max(0, groupSize - 1) >= n - 1,
        everyoneMeetsEveryone: n > 1 && minMet === n - 1,
        repeats: maxRepeat > 1,
    };
};

// One line for the Groups screen. Plain facts — the reader is an organiser
// checking the draw did what the button said.
export const coverageLine = ({ minMet, maxMet, others, rounds, enoughRounds, everyoneMeetsEveryone, repeats }) => {
    if (others === 0) return null;
    if (everyoneMeetsEveryone) {
        return repeats
            ? 'Everyone plays with everyone at least once'
            : 'Everyone plays with everyone';
    }
    const met = minMet === maxMet ? `${minMet}` : `${minMet}–${maxMet}`;
    const line = `Everyone plays with ${met} of the other ${others}`;
    if (enoughRounds) return line;
    return `${line} — ${rounds} round${rounds === 1 ? '' : 's'} can't fit everyone in`;
};
